import defer from "lodash/defer";
import * as React from "react";
import { DropTarget } from "react-dnd";
import { inject, observer } from "mobx-react";
import ReactDOM from "react-dom";
import styled from "styled-components";

import {
    DND_NEW_SECTION,
    DND_NEW_TRANSITION,
    DND_SECTION,
    allowedLayerDragSources
} from "pages/editor/lib/timeline";
import { setInlineStyles } from "util/style-utils";

import Layer from "./layer";
import DragPreview from "./dragpreview";
import TimelineSection from "./timeline-section-wrapper";
import TransitionDropAreas from "./transitionDropAreas";

function collect(connect, monitor) {
    return {
        connectDropTarget: connect.dropTarget(),
        isOver: monitor.isOver(),
        itemType: monitor.getItemType(),
        item: monitor.getItem()
    };
}
const SnapGuide = setInlineStyles(styled.div, ({ position }) => ({
    transform: `translateX(${position}px`
}))`
	width: 1px;
	height: 100%;
	position: absolute;
	background-color: #FFC461;
	z-index: ${({ theme }) => theme.snapGuideZIndex};
`;

class TimelineContainer extends React.Component {
    constructor(props) {
        super(props);
    }

    /**
     * @summary If dragging sections/new sections on the timeline we want layer drop targets to be able to receive them.
     * Normally they would be covered by other sections preventing the drop targets from receiving pointer events, which
     * is why we disable pointer events on them for the duration of the drag operation.
     */
    componentDidUpdate(prevProps) {
        const shouldExposePrev = this.shouldExposeLayers(prevProps.itemType);
        const shouldExposeCurr = this.shouldExposeLayers(this.props.itemType);

        if (!shouldExposePrev && shouldExposeCurr) {
            // If called synchronously the dragend event will be emitted
            defer(this.props.ui.disableTimelinePointerEvents);
        }

        if (shouldExposePrev && !shouldExposeCurr) {
            this.props.ui.enableTimelinePointerEvents();
        }
    }

    /**
     * @summar Check whether a drag source's type requires layer drop targets to be exposed
     */
    shouldExposeLayers(itemType) {
        return itemType === DND_NEW_SECTION || itemType === DND_SECTION;
    }

    /**
     * @summary - Drag preview layer for new sections and new transitions
     */
    renderDragPreview() {
        const { itemType } = this.props;
        if (itemType === DND_NEW_SECTION || itemType === DND_NEW_TRANSITION) {
            return ReactDOM.createPortal(
                <DragPreview />,
                document.querySelector(`body`)
            );
        }
        return null;
    }

    /**
     * @summary - Timeline sections
     */
    renderSections() {
        const { ui, composition } = this.props;
        return (
            <div
                style={{
                    zIndex: 1,
                    position: `relative`,
                    pointerEvents: ui.pointerEvents ? `all` : `none`
                }}
            >
                {composition.sections.map((section) => (
                    <TimelineSection
                        key={section.id}
                        section={section}
                        composition={composition}
                        ui={ui}
                    />
                ))}
            </div>
        );
    }

    /**
     * @summary - Timeline layers
     */
    renderLayers() {
        const {
            composition: { layersForTimeline }
        } = this.props;
        return layersForTimeline.map(({ id }) => <Layer id={id} key={id} />);
    }

    /**
     * @summary - Renders a line indicating where an asset/section will be snapped to
     * @method
     *
     * @returns {Null|React.JSXElementConstructor}
     */
    renderSnapGuide() {
        const {
            ui: { snapGuidePosition }
        } = this.props;
        if (snapGuidePosition) {
            return <SnapGuide position={snapGuidePosition} />;
        }
        return null;
    }

    /**
     * @summary - Transition drop areas
     */
    renderTransitionDropAreas() {
        const {
            ui,
            itemType,
            composition: { layersForTimeline }
        } = this.props;

        if (itemType === DND_NEW_TRANSITION) {
            return layersForTimeline.map((layer) => (
                <TransitionDropAreas key={layer.id} ui={ui} layer={layer} />
            ));
        }

        return null;
    }

    render() {
        const { connectDropTarget } = this.props;
        return (
            <div data-testid="timeline" ref={connectDropTarget}>
                {this.renderSnapGuide()}
                {this.renderDragPreview()}
                {this.renderSections()}
                {this.renderLayers()}
                {this.renderTransitionDropAreas()}
            </div>
        );
    }
}

export default inject(
    `composition`,
    `ui`
)(
    DropTarget(
        allowedLayerDragSources,
        {},
        collect
    )(observer(TimelineContainer))
);
